/**
 * CrossValidationSourceFactory.ts — F4.01
 *
 * Fábrica de adaptadores que convierte los geocodificadores existentes
 * (DERA local, CDAU R2, NGA R2, CartoCiudad, Nominatim) en la interfaz
 * GeocodingSource de lib/crossValidation, permitiendo invocar
 * performCrossValidation() sobre cualquier infraestructura.
 *
 * Selecciona 2-4 fuentes relevantes según tipología PTEL.
 *
 * @module services/crossValidation
 * @version 1.0.0 — F4.01
 */

import type {
  GeocodingSource,
  GeocodingSourceId,
  GeocodingQuery,
  GeocodingResult,
  CrossValidationResult,
} from '../../lib/crossValidation/types';

import { performCrossValidation } from '../../lib/crossValidation';
import { searchLocal, type InfrastructureCategory } from '../../lib/LocalDataService';
import { getMunicipioPorCodigo } from '../../lib/codigosINEUnificado';
import { acceptCartoCiudadResult } from '../geocoding/ineValidator';

// ============================================================================
// TIPOLOGÍAS → CATEGORÍAS LOCALES
// ============================================================================

/** Mapea tipología PTEL a categorías de LocalDataService */
const TIPOLOGIA_TO_LOCAL_CATEGORIES: Record<string, InfrastructureCategory[]> = {
  SANITARIO:      ['health'],
  EDUCATIVO:      ['education'],
  SEGURIDAD:      ['security'],
  EMERGENCIAS:    ['security'],
  ADMINISTRATIVO: ['municipal'],
  ENERGIA:        ['energy'],
  DEPORTIVO:      ['municipal'],
  CULTURAL:       ['municipal'],
  HIDRAULICO:     ['energy'],
  TRANSPORTE:     ['municipal'],
};

// ============================================================================
// ADAPTADORES DE FUENTE
// ============================================================================

/**
 * Crea un GeocodingSource que envuelve searchLocal() (DERA offline).
 */
export function createLocalDeraSource(): GeocodingSource {
  return {
    id: 'local_dera',
    name: 'DERA Local (offline)',
    authorityWeight: 0.95,
    timeoutMs: 2000,
    async geocode(query: GeocodingQuery): Promise<GeocodingResult | null> {
      const categorias = TIPOLOGIA_TO_LOCAL_CATEGORIES[
        query.tipologia?.toUpperCase() ?? ''
      ];
      const result = await searchLocal({
        nombre: query.nombre,
        codMun: query.codMun,
        municipio: query.municipio,
        categorias: categorias,
        maxResults: 1,
        threshold: 0.35,
      });

      if (!result.success || !result.bestMatch) return null;
      const m = result.bestMatch;
      return {
        coordinates: { x: m.x, y: m.y },
        matchedName: m.nombre,
        matchScore: result.matchScore,
        confidence: result.matchScore / 100,
      };
    },
  };
}

/**
 * Crea un GeocodingSource que envuelve CDAU R2 (callejero andaluz offline).
 */
export function createCdauR2Source(): GeocodingSource {
  return {
    id: 'cdau_r2',
    name: 'CDAU R2 (callejero)',
    authorityWeight: 0.82,
    timeoutMs: 5000,
    async geocode(query: GeocodingQuery): Promise<GeocodingResult | null> {
      const { getCDAUR2Geocoder } = await import(
        '../geocoding/specialized/CDAUR2Geocoder'
      );
      const geocoder = getCDAUR2Geocoder();

      // CDAU necesita dirección; si no hay, usar nombre como fallback
      const direccion = query.direccion || query.nombre;
      const result = await geocoder.geocode(direccion, query.codMun);

      if (!result) return null;
      return {
        coordinates: { x: result.x, y: result.y },
        matchedName: result.fullAddress ?? direccion,
        matchScore: result.confidence,
        confidence: result.confidence / 100,
      };
    },
  };
}

/**
 * Crea un GeocodingSource que envuelve NGA R2 (topónimos).
 */
export function createNgaR2Source(): GeocodingSource {
  return {
    id: 'nga_r2',
    name: 'NGA R2 (topónimos)',
    authorityWeight: 0.78,
    timeoutMs: 5000,
    async geocode(query: GeocodingQuery): Promise<GeocodingResult | null> {
      const { getNGAR2Geocoder } = await import(
        '../geocoding/specialized/NGAR2Geocoder'
      );
      const geocoder = getNGAR2Geocoder();

      const result = await geocoder.geocode({
        query: query.nombre,
        codINE: query.codMun,
        maxResults: 1,
      });

      if (!result) return null;
      return {
        coordinates: { x: result.x, y: result.y },
        matchedName: result.feature?.name ?? query.nombre,
        matchScore: result.score,
        confidence: result.score / 100,
      };
    },
  };
}

/**
 * Crea un GeocodingSource que envuelve CartoCiudad (online, IGN).
 */
export function createCartoCiudadSource(): GeocodingSource {
  return {
    id: 'cartociudad',
    name: 'CartoCiudad (IGN)',
    authorityWeight: 0.80,
    timeoutMs: 5000,
    async geocode(query: GeocodingQuery): Promise<GeocodingResult | null> {
      const { CartoCiudadGeocoder } = await import(
        '../geocoding/generic/CartoCiudadGeocoder'
      );
      const geocoder = new CartoCiudadGeocoder();

      // Resolver provincia a partir de codMun
      const info = getMunicipioPorCodigo(query.codMun);
      const province = info?.provincia ?? '';
      const municipality = query.municipio ?? info?.nombre ?? '';

      const result = await geocoder.geocodeInfrastructure(
        query.nombre,
        municipality,
        province,
      );

      if (!result.success) return null;
      const d = result.data;

      // Guard cross-muniCode (OBS-FT-XVAL-CARTO-CROSS-MUNI-DRIFT-GENERIC):
      // descartar respuestas con muniCode_carto != codINE esperado antes de
      // entregar al cross-validator.
      const acepta = acceptCartoCiudadResult(
        {
          muni: d.municipality,
          muniCode: d.muniCode,
          province: d.province,
          lat: d.y,
          lng: d.x,
        },
        municipality,
        province,
        query.codMun
      );
      if (!acepta) {
        return null;
      }

      return {
        coordinates: { x: d.x, y: d.y },
        matchedName: d.matchedName,
        matchScore: d.confidence,
        confidence: d.confidence / 100,
      };
    },
  };
}

/**
 * Crea un GeocodingSource que envuelve Nominatim (online, OSM).
 */
export function createNominatimSource(): GeocodingSource {
  return {
    id: 'nominatim',
    name: 'Nominatim (OSM)',
    authorityWeight: 0.55,
    timeoutMs: 5000,
    async geocode(query: GeocodingQuery): Promise<GeocodingResult | null> {
      const { NominatimGeocoder } = await import(
        '../geocoding/generic/NominatimGeocoder'
      );
      const geocoder = new NominatimGeocoder();

      const info = getMunicipioPorCodigo(query.codMun);
      const municipality = query.municipio ?? info?.nombre ?? '';
      const province = info?.provincia ?? '';

      const result = await geocoder.geocodeInfrastructure(
        query.nombre,
        municipality,
        province,
      );
      if (!result.success) return null;
      const d = result.data;

      return {
        coordinates: { x: d.x, y: d.y },
        matchedName: d.matchedName,
        matchScore: d.confidence,
        confidence: d.confidence / 100,
      };
    },
  };
}

// ============================================================================
// OPT-01: RESULTADO PRECOMPUTADO — evita llamadas duplicadas desde la cascada
// ============================================================================

/**
 * Resultado de geocodificación ya realizado por la cascada principal.
 * Al inyectarlo en crossValidateInfrastructure() se evita repetir la
 * misma llamada HTTP dentro del motor de validación cruzada.
 *
 * Caso de uso principal: cascada L8 (CDAU online) ya ejecutada
 * → no volver a llamar a CDAU R2 en la validación cruzada.
 */
export interface PrecomputedSourceResult {
  /** ID del GeocodingSource a sustituir (debe ser un literal de GeocodingSourceId) */
  sourceId: GeocodingSourceId;
  /** Coordenadas ya computadas (EPSG:25830) */
  coordinates: { x: number; y: number };
  /** Nombre o dirección encontrada por el geocodificador */
  matchedName: string;
  /** Confianza 0–100 (escala PTEL) */
  confidence: number;
}

/**
 * Crea un GeocodingSource que devuelve un resultado pre-computado
 * sin realizar ninguna llamada HTTP.
 *
 * La authorityWeight se mantiene igual que la del adaptador en vivo
 * (0.82 para CDAU R2) para que el motor de clustering no penalice
 * el resultado respecto al caso en que se llama en tiempo real.
 */
export function createPrecomputedSource(pre: PrecomputedSourceResult): GeocodingSource {
  return {
    id: pre.sourceId,
    name: `${pre.sourceId} (precomputado)`,
    authorityWeight: 0.82, // idéntico a createCdauR2Source()
    timeoutMs: 0,
    async geocode(_query: GeocodingQuery): Promise<GeocodingResult | null> {
      return {
        coordinates: pre.coordinates,
        matchedName: pre.matchedName,
        matchScore: pre.confidence,
        confidence: pre.confidence / 100,
      };
    },
  };
}

// ============================================================================
// FÁBRICA DE FUENTES POR TIPOLOGÍA
// ============================================================================

/** Configuración de selección de fuentes */
export interface SourceSelectionOptions {
  /** Incluir fuentes online (default: true) */
  includeOnline?: boolean;
  /** Máximo de fuentes (default: 4) */
  maxSources?: number;
}

/**
 * Selecciona las fuentes más apropiadas para una tipología PTEL.
 *
 * Lógica:
 * - Siempre incluye LOCAL_DERA (offline, máxima autoridad).
 * - Añade CDAU R2 si la infraestructura tiene dirección probable.
 * - Añade NGA R2 para topónimos rurales / hidráulico / energía.
 * - Añade CartoCiudad online si se permite.
 * - Nominatim solo como 4.ª fuente si faltan otras.
 *
 * @param tipologia — Tipología PTEL (SANITARIO, EDUCATIVO, etc.)
 * @param options — Opciones de selección
 * @returns Array de GeocodingSource[] (2-4 fuentes)
 */
export function selectSourcesForTypology(
  tipologia?: string,
  options: SourceSelectionOptions = {},
): GeocodingSource[] {
  const { includeOnline = true, maxSources = 4 } = options;
  const tipo = tipologia?.toUpperCase() ?? '';
  const sources: GeocodingSource[] = [];

  // 1. Siempre: DERA local offline
  sources.push(createLocalDeraSource());

  // 2. CDAU R2 para tipologías con dirección (urbanas)
  const urbanas = new Set([
    'SANITARIO', 'EDUCATIVO', 'SEGURIDAD', 'EMERGENCIAS',
    'ADMINISTRATIVO', 'DEPORTIVO', 'CULTURAL',
  ]);
  if (urbanas.has(tipo)) {
    sources.push(createCdauR2Source());
  }

  // 3. NGA R2 para tipologías rurales / topónimos
  const rurales = new Set(['HIDRAULICO', 'ENERGIA', 'TRANSPORTE']);
  if (rurales.has(tipo) || !urbanas.has(tipo)) {
    sources.push(createNgaR2Source());
  }

  // OBS-FT-XVAL-SEG-COBERTURA-INSUFICIENTE-EMERGENCIAS-CONFIRMADA (L79 · SUBA2 18-may):
  // CartoCiudad no resuelve instalaciones administrativas tipo CECEM/CECOPAL/112
  // (cobertura útil 5,00 % empírica n=20 features). Excluir para evitar inflar
  // fallos D en el cross-validator. Cubre variante TD-EMERGENCIA legacy sin S
  // (validationStrategy.ts:165-178).
  const tipologiaExcluyeCartoCiudad = new Set(['EMERGENCIAS', 'EMERGENCIA']);
  const excluirCarto = tipologiaExcluyeCartoCiudad.has(tipo);

  // 4. CartoCiudad online (si se permite Y la tipología la admite)
  if (includeOnline && sources.length < maxSources && !excluirCarto) {
    sources.push(createCartoCiudadSource());
  }

  // 5. Nominatim como relleno si faltan fuentes
  //    DOP-L79-NOMINATIM=β (smoke 18-may n=5: 1 hit útil + 1 falso positivo
  //    cross-muniCode Plaza España Vejer vs Cádiz cap). Sucesora
  //    OBS-FT-XVAL-SEG-COBERTURA-NOMINATIM-EMERGENCIAS sev D doc-only.
  if (includeOnline && sources.length < 2) {
    sources.push(createNominatimSource());
  }

  return sources.slice(0, maxSources);
}

// ============================================================================
// FUNCIÓN PRINCIPAL DE CONVENIENCIA
// ============================================================================

/** Parámetros para validación cruzada de una infraestructura */
export interface CrossValidateParams {
  /** Nombre de la infraestructura */
  nombre: string;
  /** Código INE del municipio (5 dígitos) */
  codMun: string;
  /** Tipología PTEL */
  tipologia?: string;
  /** Nombre del municipio (opcional, se resuelve desde codMun) */
  municipio?: string;
  /** Dirección (mejora match en CDAU/CartoCiudad) */
  direccion?: string;
  /** Opciones de selección de fuentes */
  sourceOptions?: SourceSelectionOptions;
  /** Timeout global en ms (default: 15 000) */
  timeoutMs?: number;
  /**
   * OPT-01: Resultados pre-computados por la cascada principal.
   * El motor sustituye el GeocodingSource cuyo `id` coincida con
   * `precomputed[i].sourceId` por una fuente local que devuelve el
   * valor ya conocido, eliminando la llamada HTTP redundante.
   */
  precomputed?: PrecomputedSourceResult[];
}

/**
 * Realiza validación cruzada completa de una infraestructura.
 *
 * Flujo:
 * 1. Resuelve municipio/provincia desde codMun
 * 2. Selecciona fuentes según tipología
 * 3. Invoca performCrossValidation() (paralelo, cluster, scoring)
 * 4. Devuelve CrossValidationResult
 *
 * @example
 * const result = await crossValidateInfrastructure({
 *   nombre: 'Centro de Salud Berja',
 *   codMun: '04031',
 *   tipologia: 'SANITARIO',
 * });
 * if (result.recommendation === 'USE_RESULT') {
 *   console.log('Coordenada validada:', result.recommendedCoordinate);
 * }
 */
export async function crossValidateInfrastructure(
  params: CrossValidateParams,
): Promise<CrossValidationResult> {
  const {
    nombre,
    codMun,
    tipologia,
    direccion,
    sourceOptions,
    timeoutMs = 15_000,
    precomputed,
  } = params;

  // Resolver municipio si no se proporciona
  const info = getMunicipioPorCodigo(codMun);
  const municipio = params.municipio ?? info?.nombre;

  // Construir query
  const query: GeocodingQuery = {
    nombre,
    codMun,
    municipio,
    tipologia,
    direccion,
  };

  // OPT-01: seleccionar fuentes y sustituir las que ya se han ejecutado
  // en la cascada principal por su resultado precomputado (sin HTTP).
  let sources = selectSourcesForTypology(tipologia, sourceOptions);
  if (precomputed?.length) {
    sources = sources.map((src) => {
      const pre = precomputed.find((p) => p.sourceId === src.id);
      return pre ? createPrecomputedSource(pre) : src;
    });
  }

  // Ejecutar validación cruzada
  return performCrossValidation(query, sources, { timeoutMs });
}
